import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {assertAndContinue} from 'Asserts';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {setValueInConfig} from 'pipelines/generalized-step-config-editor/step-config-settings/StepConfigSettingsUtils';
import * as PipelineDAO from 'pipelines/PipelineDAO';
import {validatePipelineStepConfig} from 'pipelines/step-config-validation/validatePipelineStepConfig';
import {GetPipelineConfigSpecType, getStepTypeConfigShape} from 'pipelines/StepShapeUtils';
import {InlineBannerType} from 'ui-library/banners/InlineBanner';
import InlineBanner from 'ui-library/banners/InlineBanner';
import InlineBannerStack from 'ui-library/banners/InlineBannerStack';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import usePrevious from 'ui-library/hooks/usePrevious';
import LoadingOverlay from 'ui-library/LoadingOverlay';
import {assertJSONObject, JSONValue} from 'util/Types';

import StepConfigEditorRouter from './editors/StepConfigEditorRouter';
import StepConfigEditorRouterV2 from './editors/StepConfigEditorRouterV2';
import makeInitialConfigState from './initial-values/makeInitialConfigState';
import {STEP_CONFIG_SETTINGS, STEP_CONFIG_SETTINGS_V2} from './step-config-settings/StepConfigSettings';
import {StepConfigEditorContext} from './StepConfigEditorContext';
import {
  getInputDataFramesFromInfos,
  StepConfigUISchemaV2,
  StepConfigUISchema,
  ConfigDataFrameInfo,
  HAS_BACKEND_FIELD_GENERATOR,
} from './StepConfigEditorTypes';
import {
  ConfigEditorStepType,
  stepTypeIsConfigEditorSupported,
  isUiSchemaForStepTypeV2,
} from './stepTypeIsConfigEditorSupported';

const FAILED_STEP_WARNING_BANNER_READ_ONLY_TEXT =
  'The configuration for this transformation is no longer valid.';

const stepShapeOverrideGlobalState = makeUseGlobalClientState<GetPipelineConfigSpecType<'shape'> | null>(
  null
);

/** @package */
export const FAILED_STEP_WARNING_BANNER_TEXT = `${FAILED_STEP_WARNING_BANNER_READ_ONLY_TEXT} Please reconfigure it.`;

interface StepConfigEditorPaneProps {
  stepType: ConfigEditorStepType;
  stepVersion: number;
  config: Record<string, JSONValue> | null;
  setConfig: (newConfig: Record<string, JSONValue>) => void;
  dataFramesInfos: Record<string, Array<ConfigDataFrameInfo>>;
  clientStateId: string;
  error?: string;
  stepIsFailed?: boolean;
  readOnly?: boolean;
  forceSingleColumnDisplay?: boolean;
  stepId?: string;
}

export default function StepConfigEditorPane({
  stepType,
  stepVersion,
  dataFramesInfos,
  config,
  setConfig,
  clientStateId,
  error,
  stepIsFailed,
  readOnly,
  forceSingleColumnDisplay,
  stepId,
}: StepConfigEditorPaneProps): JSX.Element {
  // Memoize so that the context provider doesn't re-render the entire tree every time this component
  // rerenders e.g. due to a state change (also because we'd get an eslint error otherwise)
  const contextValue = useMemo(() => ({clientStateId, dataFramesInfos}), [dataFramesInfos, clientStateId]);
  // Step shape override - only used when backend field generation is used to override default step shape.
  const [stepShapeOverride, setStepShapeOverride] =
    stepShapeOverrideGlobalState.useGlobalClientState(clientStateId);

  let uiSchema: StepConfigUISchema | StepConfigUISchemaV2;
  if (isUiSchemaForStepTypeV2(stepType)) {
    uiSchema = STEP_CONFIG_SETTINGS_V2[stepType][stepVersion].uiSchema;
  } else {
    uiSchema = STEP_CONFIG_SETTINGS[stepType][stepVersion].uiSchema;
  }
  const hasBackendFieldGeneration = uiSchema?.[HAS_BACKEND_FIELD_GENERATOR] ?? false;
  // Only used if step has backend field generation.
  const [isLoadingBackendFields, setIsLoadingBackendFields] = useState<boolean>(false);

  const generateBackendFields = useCallback(
    (newConfig: Record<string, JSONValue>) => {
      setIsLoadingBackendFields(true);
      async function generateConfig(): Promise<void> {
        const response = await PipelineDAO.generateStepConfig(
          getStepTypeConfigShape(stepType, stepVersion),
          newConfig,
          stepType,
          stepId
        );
        setStepShapeOverride(response.stepShape);
        setConfig(response.config);
        setIsLoadingBackendFields(false);
      }
      generateConfig();
    },
    [stepType, stepId, stepVersion, setStepShapeOverride, setConfig]
  );

  const previousStepType = usePrevious(stepType);
  const previousStepId = usePrevious(stepId);
  useEffect(() => {
    // Only update step shape / maybe call backend field API if step type or step itself changed.
    if (stepType === previousStepType && stepId === previousStepId) {
      return;
    }
    setStepShapeOverride(null);
    if (hasBackendFieldGeneration) {
      generateBackendFields(config ?? makeInitialConfigState(stepType, stepVersion));
    }
  });

  if (!stepTypeIsConfigEditorSupported(stepType, stepVersion) || stepType === PipelineStepType.CHART_VIEW) {
    assertAndContinue(false, `StepConfigEditorPane used for ${stepType} v${stepVersion}`);
    return <InlineBanner bannerType={InlineBannerType.WARNING} text="No configuration is available." />;
  }

  const errors = [
    ...(error ? [error] : []),
    ...(config
      ? validatePipelineStepConfig(
          stepType,
          stepVersion,
          config,
          getInputDataFramesFromInfos(dataFramesInfos)
        ).errors
      : []),
  ];

  return (
    <LoadingOverlay isLoading={isLoadingBackendFields} isFaded={true}>
      <StepConfigEditorContext.Provider value={contextValue}>
        {isUiSchemaForStepTypeV2(stepType) ? (
          <StepConfigEditorRouterV2
            configShape={stepShapeOverride ?? getStepTypeConfigShape(stepType, stepVersion)}
            config={config ?? makeInitialConfigState(stepType, stepVersion)}
            dataTestAttrs={{}}
            onChangeConfig={(
              oldConfig: Record<string, JSONValue | null>,
              path: Array<string>,
              newValue: JSONValue
            ) => {
              const newConfig = setValueInConfig(oldConfig ?? {}, path, newValue);
              if (hasBackendFieldGeneration) {
                generateBackendFields(newConfig);
              } else {
                setConfig(newConfig);
              }
            }}
            uiSchema={uiSchema as StepConfigUISchemaV2}
            disabled={readOnly}
          />
        ) : (
          <StepConfigEditorRouter
            configShape={stepShapeOverride ?? getStepTypeConfigShape(stepType, stepVersion)}
            config={config ?? makeInitialConfigState(stepType, stepVersion)}
            dataTestAttrs={{}}
            onChangeConfig={(config) => {
              // This should be true as long as we have a list of named variables as the step config shape.
              assertJSONObject(config);
              if (hasBackendFieldGeneration) {
                generateBackendFields(config);
              } else {
                setConfig(config);
              }
            }}
            uiSchema={uiSchema as StepConfigUISchema}
            isTopLevel={true}
            disabled={readOnly}
            forceSingleColumnDisplay={forceSingleColumnDisplay}
          />
        )}
        <InlineBannerStack
          messages={errors.map((error) => ({bannerType: InlineBannerType.WARNING, text: error}))}
        />
        {stepIsFailed && (
          <InlineBanner
            bannerType={InlineBannerType.WARNING}
            text={readOnly ? FAILED_STEP_WARNING_BANNER_READ_ONLY_TEXT : FAILED_STEP_WARNING_BANNER_TEXT}
          />
        )}
      </StepConfigEditorContext.Provider>
    </LoadingOverlay>
  );
}
